import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Paper from '@mui/material/Paper';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { makeStyles } from '@mui/styles';
import './DraggableAppModal.scss';

const useStyles = makeStyles({
  unsetOverflowY: {
    overflowY: 'unset',
    maxWidth: 'unset',
  },
  dialog: {
    position: 'absolute',
    top: 0,
    margin: 0,
  },
  paper: {
    cursor: 'move',
  },
  dialogTitle: {
    cursor: 'move',
  },
});

// The unsetOverflowY class ties with MUI's own paper styles on specificity, so
// which one wins depends on stylesheet injection order at runtime — a boot-order
// change once flipped it and capped every dialog at its breakpoint width.
// Inline styles are immune to that race.
const unsetPaperStyle = { maxWidth: 'unset', overflowY: 'unset' };

function PaperComponent(props) {
  const { dragBounds, ...paperProps } = props;
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], input, textarea, select, button, [role="button"], [class*="MuiSelect"], [class*="MuiInput"], [class*="MuiButton"]'}
      bounds={dragBounds}
    >
      <Paper {...paperProps} />
    </Draggable>
  );
}

export default function DraggableAppModal(props) {

  const classes = useStyles();

  const handleClose = (event, reason) => {
    // Don't close on backdrop click or escape key when hideBackdrop is true
    if (props.hideBackdrop && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (props.onClose) {
      props.onClose();
    }
  };
  
  // No need to block clicks - the hideBackdrop prop handles this correctly
  
  return (
    <div className={props.hideBackdrop ? "draggable-modal-override" : ""}>
    <Dialog
      fullWidth={props.fullWidth}
      maxWidth={props.maxWidth}
      open={props.open}
      onClose={handleClose}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={PaperComponent}
      PaperProps={{
        dragBounds: props.dragBounds,
        style: props.onTop
          ? props.paperStyle
          : { ...unsetPaperStyle, ...(props.paperStyle || {}) },
        className: props.paperClassName,
      }}
      classes={{
        paper: props.onTop ? classes.dialog : classes.unsetOverflowY,
      }}
      disablePortal={props.disablePortal || false}
      hideBackdrop={props.hideBackdrop || false}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      disableScrollLock={true}
      BackdropProps={{
        invisible: props.hideBackdrop
      }}
    >
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title" className={`${classes.dialogTitle} ${props.titleClassName || ''}`}>
        <Grid container wrap="nowrap" spacing={2}>

          <Grid item xs={12}>
            <Typography variant='h3'>
              {props.title}
            </Typography>
          </Grid>

          {props.headerActions &&
            <Grid item>
              {props.headerActions}
            </Grid>
          }

          {props.closeIcon &&
            <Grid item>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          }

        </Grid>
      </DialogTitle>
      <DialogContent classes={{ root: classes.unsetOverflowY}} style={{ overflowY: 'unset' }}>

        {props.content}

      </DialogContent>
      <DialogActions>
        { props.actions &&
          props.actions
        }
      </DialogActions>
    </Dialog>
    </div>
  );
}

DraggableAppModal.propTypes = {
  actions: PropTypes.node,
  closeIcon: PropTypes.bool,
  content: PropTypes.node,
  fullWidth: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  onClose: PropTypes.func,
  onTop: PropTypes.bool,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  titleClassName: PropTypes.string,
  paperClassName: PropTypes.string,
  headerActions: PropTypes.node,
  dragBounds: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hideBackdrop: PropTypes.bool,
  paperStyle: PropTypes.object,
};

DraggableAppModal.defaultProps = {
  fullWidth: false,
  maxWidth: 'lg',
  open: false,
  hideBackdrop: false,
};
DraggableAppModal.contextTypes = {
  t: PropTypes.func.isRequired
};
